import axios from 'axios';
import config from '../config';

/** Mismo origen HTTPS en producción; evita Mixed Content con URLs http://IP antiguas. */
const getApiBaseUrl = () => {
  if (typeof window !== 'undefined' && window.location?.origin) {
    return `${window.location.origin}/api`;
  }
  return config.apiUrl;
};

// Configuración base de Axios
const api = axios.create({
  baseURL: getApiBaseUrl(),
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Interceptor para agregar token a las peticiones
api.interceptors.request.use(
  (requestConfig) => {
    requestConfig.baseURL = getApiBaseUrl();
    const token = localStorage.getItem('token');
    if (token) {
      requestConfig.headers.Authorization = `Bearer ${token}`;
    }
    return requestConfig;
  },
  (error) => Promise.reject(error)
);

// Interceptor para manejar errores
api.interceptors.response.use(
  (response) => response,
  (error) => {
    console.error('API Error:', error);
    
    // Para errores 400 (Bad Request), pasar el error completo del servidor
    if (error.response?.status === 400) {
      return Promise.reject(error);
    }
    
    // Para errores 401/403, verificar si es un error de login o de token inválido
    if (error.response?.status === 401 || error.response?.status === 403) {
      // Si es un error de login (no tiene token o el error viene del endpoint de login)
      if (!localStorage.getItem('token') || error.config?.url?.includes('/auth/login')) {
        // Pasar el error completo para que se maneje en el componente
        return Promise.reject(error);
      }
      
      // Si es un error de token inválido en otras rutas, redirigir al login
      if (!error.response?.data?.requiereAprobacion) {
        localStorage.removeItem('token');
        window.location.href = '/login';
      }
    }
    
    if (error.response?.data?.error) {
      return Promise.reject(new Error(error.response.data.error));
    }
    const st = error.response?.status;
    if (st === 502 || st === 503 || st === 504) {
      return Promise.reject(
        new Error('Servidor temporalmente no disponible. Intenta de nuevo en unos segundos.')
      );
    }
    return Promise.reject(new Error('Error de conexión'));
  }
);

// Servicios para expedientes
export const expedientesAPI = {
  // Obtener lista de expedientes
  getExpedientes: (params = {}) => 
    api.get('/expedientes', { params }),

    // Obtener expediente por ID
  getExpediente: (id) =>
    api.get(`/expedientes/${id}`),

  getContratoConstruccion: (id) =>
    api.get(`/expedientes/${id}/contrato-construccion`),

  generarContratoConstruccion: (id, confirmar = false) =>
    api.post(`/expedientes/${id}/contrato-construccion/generar`, { confirmar }),

  // Crear expediente
  createExpediente: (data) =>
    api.post('/expedientes', data),

  // Actualizar expediente
  updateExpediente: (id, data) =>
    api.patch(`/expedientes/${id}`, data),

  // Eliminar expediente
  deleteExpediente: (id) => 
    api.delete(`/expedientes/${id}`),

  // Restaurar expediente
  restoreExpediente: (id) => 
    api.patch(`/expedientes/${id}/restore`),

  // Obtener clientes
  getClientes: () => 
    api.get('/clientes'),

    // Crear nuevo cliente
  createCliente: (data) =>
    api.post('/clientes', data),

  // Actualizar cliente
  updateCliente: (id, data) =>
    api.patch(`/clientes/${id}`, data),

  // Eliminar cliente
  deleteCliente: (id) =>
    api.delete(`/clientes/${id}`),

  // Buscar DNI en API MIGO
  buscarDNI: (dni) =>
    api.get(`/clientes/buscar-dni/${dni}`),

  // Generar boleta final
  generarBoletaFinal: (id, data) =>
    api.post(`/expedientes/${id}/generar-boleta`, data),
};

// Servicios para partes
export const partesAPI = {
  // Obtener partes de un expediente
  getPartes: (expedienteId) => 
    api.get(`/partes/expediente/${expedienteId}`),

  // Crear parte
  createParte: (expedienteId, data) => 
    api.post(`/partes/expediente/${expedienteId}`, data),

  // Actualizar parte
  updateParte: (id, data) => 
    api.patch(`/partes/${id}`, data),

  // Eliminar parte
  deleteParte: (id) => 
    api.delete(`/partes/${id}`),

  // Marcar parte como completada
  completarParte: (id) => 
    api.patch(`/partes/${id}/completar`),

  // Marcar parte como pendiente
  marcarPendiente: (id) => 
    api.patch(`/partes/${id}/pendiente`),
};

// Servicios para abonos
export const abonosAPI = {
  // Obtener abonos de un expediente
  getAbonos: (expedienteId) => 
    api.get(`/abonos/expediente/${expedienteId}`),

  // Crear abono
  createAbono: (expedienteId, data) => 
    api.post(`/abonos/expediente/${expedienteId}`, data),

  // Crear abono con recibo automático
  createAbonoConRecibo: (expedienteId, data) => 
    api.post(`/abonos/expediente/${expedienteId}/con-recibo`, data),

  // Crear abono con evidencia
  createAbonoConEvidencia: (expedienteId, formData) => 
    api.post(`/abonos/expediente/${expedienteId}/con-evidencia`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }),

  // Actualizar abono
  updateAbono: (id, data) => 
    api.patch(`/abonos/${id}`, data),

  // Eliminar abono
  deleteAbono: (id) => 
    api.delete(`/abonos/${id}`),
};

// Servicios para costos adicionales del expediente
export const costosExpedienteAPI = {
  getCostos: (expedienteId) =>
    api.get(`/costos-expediente/expediente/${expedienteId}`),

  createCosto: (expedienteId, data) =>
    api.post(`/costos-expediente/expediente/${expedienteId}`, data),

  updateCosto: (id, data) =>
    api.patch(`/costos-expediente/${id}`, data),

  deleteCosto: (id) =>
    api.delete(`/costos-expediente/${id}`),
};

// Servicios para archivos
export const archivosAPI = {
  // Obtener archivos de un expediente
  getArchivos: (expedienteId) => 
    api.get(`/archivos/expediente/${expedienteId}`),

  // Subir archivos
  uploadArchivos: (expedienteId, formData) => 
    api.post(`/archivos/expediente/${expedienteId}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }),

  // Eliminar archivo
  deleteArchivo: (id) => 
    api.delete(`/archivos/${id}`),

  // Descargar archivo
  downloadArchivo: (id) => 
    api.get(`/archivos/${id}/descargar`, { responseType: 'blob' }),
};

// Servicios para tipos
export const tiposAPI = {
  // Obtener tipos
  getTipos: () =>
    api.get('/tipos'),

  // Crear tipo
  createTipo: (data) =>
    api.post('/tipos', data),

  // Actualizar tipo
  updateTipo: (id, data) =>
    api.patch(`/tipos/${id}`, data),

  // Eliminar tipo
  deleteTipo: (id) =>
    api.delete(`/tipos/${id}`),
};

// Servicios para clientes
export const clientesAPI = {
  // Obtener clientes
  getClientes: () =>
    api.get('/clientes'),

  // Obtener cliente por ID
  getCliente: (id) =>
    api.get(`/clientes/${id}`),

  // Crear cliente
  createCliente: (data) =>
    api.post('/clientes', data),

  // Actualizar cliente
  updateCliente: (id, data) =>
    api.patch(`/clientes/${id}`, data),

  // Eliminar cliente
  deleteCliente: (id) =>
    api.delete(`/clientes/${id}`),

  // Buscar persona por DNI en API MIGO
  buscarPorDNI: (dni) =>
    api.get(`/clientes/buscar-dni/${dni}`),
};

// Servicios para exportación
export const exportAPI = {
  // Exportar expedientes
  exportarExpedientes: (options) =>
    api.post('/export/expedientes', options, {
      responseType: 'blob'
    }),
};

// Servicios para partes preestablecidas
export const partesPreestablecidasAPI = {
  // Obtener partes por tipo (solo JEFE/ADMIN)
  getPartesByTipo: (tipoId) =>
    api.get(`/partes-preestablecidas/tipo/${tipoId}`),
  
  // Obtener todas las partes
  getPartes: () =>
    api.get('/partes-preestablecidas'),
  
  // Crear parte
  createParte: (data) =>
    api.post('/partes-preestablecidas', data),
  
  // Actualizar parte
  updateParte: (id, data) =>
    api.patch(`/partes-preestablecidas/${id}`, data),
  
  // Eliminar parte
  deleteParte: (id) =>
    api.delete(`/partes-preestablecidas/${id}`)
};

// Servicios para control de versiones
export const versionesAPI = {
  // Obtener versiones de un archivo
  getVersions: (archivoId) =>
    api.get(`/version/archivo/${archivoId}`),

  // Crear nueva versión
  createVersion: (archivoId, formData) =>
    api.post(`/version/archivo/${archivoId}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }),

  // Revertir a una versión anterior
  revertVersion: (archivoId, versionId) =>
    api.post(`/version/archivo/${archivoId}/revertir/${versionId}`),

  // Descargar una versión específica
  downloadVersion: (archivoId, versionId) =>
    api.get(`/version/archivo/${archivoId}/version/${versionId}/download`, {
      responseType: 'blob'
    }),

  // Eliminar una versión
  deleteVersion: (archivoId, versionId) =>
    api.delete(`/version/archivo/${archivoId}/version/${versionId}`),

  // Obtener estadísticas de versiones
  getEstadisticas: () =>
    api.get('/versiones/estadisticas'),
};

// Servicios para explorador de carpetas
export const foldersAPI = {
  // Obtener estructura de carpetas de un expediente
  getExpedienteFolders: (expedienteId) =>
    api.get(`/folders/expediente/${expedienteId}`),

  // Obtener archivos de una parte específica
  getParteFiles: (parteId) =>
    api.get(`/folders/parte/${parteId}`),

  // Subir archivo a una parte específica
  uploadToParte: (parteId, formData) =>
    api.post(`/folders/parte/${parteId}/upload`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }),

  // Eliminar archivo de una parte
  deleteParteFile: (parteId, fileName) =>
    api.delete(`/folders/parte/${parteId}/file/${fileName}`),
};

// Papelera de archivos eliminados (JEFE / ADMIN)
export const papeleraAPI = {
  listar: () => api.get('/papelera'),
  restaurar: (id) => api.post(`/papelera/${id}/restaurar`),
  obtenerArchivo: (id, params = {}) =>
    api.get(`/papelera/${id}/archivo`, { params, responseType: 'blob' }),
};

// Servicios para autenticación
export const authAPI = {
  // Login
  login: (data) =>
    api.post('/auth/login', data),

  // Registro
  register: (data) =>
    api.post('/auth/register', data),

  // Obtener información del usuario actual
  getMe: () =>
    api.get('/auth/me'),

  // Logout
  logout: () =>
    api.post('/auth/logout'),

  // Actualizar perfil
  updateProfile: (profileData) =>
    api.patch('/auth/profile', profileData),

  // Cambiar contraseña
  changePassword: (data) =>
    api.post('/auth/change-password', data),
};

// Servicios para dashboard
export const dashboardAPI = {
  // Obtener KPIs
  getKPIs: () => 
    api.get('/dashboard/kpis'),

  // Obtener deudas por cliente
  getDeudasPorCliente: (params = {}) => 
    api.get('/dashboard/deudas', { params }),

  // Obtener vencimientos
  getVencimientos: (dias = 7) => 
    api.get('/dashboard/vencimientos', { params: { dias } }),

  // Obtener resumen ejecutivo
  getResumen: () => 
    api.get('/dashboard/resumen'),

  // Obtener ingresos mensuales
  getIngresosMensuales: () => 
    api.get('/dashboard/ingresos-mensuales'),
};

export const dashboardAvanzadoAPI = {
  getDatos: (params) =>
    api.get('/dashboard-avanzado/datos', { params }),
  getDatosLaborales: (params) =>
    api.get('/dashboard-avanzado/datos-laborales', { params }),
  getDatosLaboralesMatriz: (params) =>
    api.get('/dashboard-avanzado/datos-laborales-matriz', { params }),
  getEstadisticas: (params) =>
    api.get('/dashboard-avanzado/estadisticas', { params }),
  getHorasMo: (params) =>
    api.get('/dashboard-avanzado/horas-mo', { params }),
  actualizarExpedienteAdmin: (id, data) =>
    api.patch(`/dashboard-avanzado/expediente/${id}/admin`, data),
};

// Función para copiar al portapapeles
export const copyToClipboard = async (text) => {
  try {
    // Método moderno (requiere HTTPS)
    if (navigator.clipboard && navigator.clipboard.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
    
    // Método de respaldo para HTTP o navegadores antiguos
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.left = '-999999px';
    textArea.style.top = '-999999px';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    
    try {
      const successful = document.execCommand('copy');
      document.body.removeChild(textArea);
      return successful;
    } catch (err) {
      document.body.removeChild(textArea);
      throw err;
    }
  } catch (err) {
    console.error('Error al copiar al portapapeles:', err);
    return false;
  }
};

// Función para formatear fechas (zona horaria de Perú)
export const formatDate = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleDateString('es-PE', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    timeZone: 'America/Lima'
  });
};

/**
 * Fecha límite / solo día civil: el valor en BD suele venir como instante UTC
 * (p. ej. medianoche del día elegido). Formatear en Lima desplaza el calendario
 * un día atrás; aquí se usa el día en UTC para coincidir con la fecha elegida.
 */
export const formatFechaLimite = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return '';
  return date.toLocaleDateString('es-PE', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    timeZone: 'UTC'
  });
};

// Función para formatear hora (zona horaria de Perú)
export const formatTime = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleTimeString('es-PE', {
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'America/Lima'
  });
};

// Función para formatear moneda
export const formatCurrency = (amount) => {
  return new Intl.NumberFormat('es-PE', {
    style: 'currency',
    currency: 'PEN'
  }).format(amount);
};

// Función para obtener clase de porcentaje
export const getPorcentajeClass = (porcentaje) => {
  if (porcentaje < 40) return 'porcentaje-bajo';
  if (porcentaje < 100) return 'porcentaje-medio';
  return 'porcentaje-alto';
};

// Servicios para aprobaciones
export const approvalsAPI = {
  // Obtener solicitudes pendientes
  getPendingApprovals: () =>
    api.get('/approvals/pending'),

  // Obtener historial de solicitudes
  getApprovalHistory: () =>
    api.get('/approvals/history'),

  // Obtener estadísticas de aprobaciones
  getApprovalStats: () =>
    api.get('/approvals/stats'),

  // Aprobar solicitud
  approveRequest: (solicitudId, data) =>
    api.post(`/approvals/${solicitudId}/approve`, data),

  // Rechazar solicitud
  rejectRequest: (solicitudId, data) =>
    api.post(`/approvals/${solicitudId}/reject`, data),
};

// Servicios para copias de seguridad
// Timeouts muy altos para copia completa (~1.5 GB): creación y descarga pueden tardar 20+ min
const BACKUP_CREATE_TIMEOUT = 3600000;  // 60 minutos (crear ZIP completo)
const BACKUP_DOWNLOAD_TIMEOUT = 3600000; // 60 minutos (descargar 1.5 GB)

export const backupAPI = {
  // Crear copia de seguridad (timeout 60 min para copia completa)
  createBackup: (type, signal) =>
    api.post('/backup/create', { type }, { timeout: BACKUP_CREATE_TIMEOUT, signal }),

  // Obtener historial de backups
  getBackupHistory: () =>
    api.get('/backup/history'),

  // Obtener estadísticas de backups
  getBackupStats: () =>
    api.get('/backup/stats'),

  // Descargar backup (timeout 60 min, progreso real y cancelación)
  downloadBackup: (backupId, onProgress, signal) =>
    api.get(`/backup/download/${backupId}`, {
      responseType: 'blob',
      timeout: BACKUP_DOWNLOAD_TIMEOUT,
      signal,
      onDownloadProgress: (progressEvent) => {
        if (onProgress && progressEvent.total) {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          onProgress({
            percent: percentCompleted,
            loaded: progressEvent.loaded,
            total: progressEvent.total
          });
        }
      }
    }),

  // Eliminar backup
  deleteBackup: (backupId) =>
    api.delete(`/backup/${backupId}`),
};

// API de versiones (nueva lógica)
export const versionAPI = {
  getVersions: (archivoId) =>
    api.get(`/version/archivo/${archivoId}`),

  createVersion: (archivoId, formData) =>
    api.post(`/version/archivo/${archivoId}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }),

  downloadVersion: (archivoId, versionId) =>
    api.get(`/version/archivo/${archivoId}/version/${versionId}/download`, {
      responseType: 'blob'
    }),

  deleteVersion: (archivoId, versionId) =>
    api.delete(`/version/archivo/${archivoId}/version/${versionId}`)
};

// Servicios para notas
export const notasAPI = {
  // Obtener notas de un expediente
  getNotas: (expedienteId) =>
    api.get(`/notas/expediente/${expedienteId}`),

  // Obtener una nota específica
  getNota: (id) =>
    api.get(`/notas/${id}`),

  // Crear nota
  createNota: (expedienteId, formData) =>
    api.post(`/notas/expediente/${expedienteId}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }),

  // Eliminar nota
  deleteNota: (id) =>
    api.delete(`/notas/${id}`)
};

// Servicios para levantamiento de requisitos
export const levantamientoAPI = {
  // Verificar si existe levantamiento
  getLevantamiento: (expedienteId) =>
    api.get(`/levantamiento/expediente/${expedienteId}`),

  // Subir levantamiento de requisitos
  subirLevantamiento: (expedienteId, formData) =>
    api.post(`/levantamiento/expediente/${expedienteId}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }),

  // Eliminar levantamiento (solo admin/jefe)
  eliminarLevantamiento: (expedienteId) =>
    api.delete(`/levantamiento/expediente/${expedienteId}`)
};

// Lista de pendientes (por usuario, persistida en servidor)
export const pendientesListaAPI = {
  listar: () => api.get('/pendientes-lista'),
  crear: (data) => api.post('/pendientes-lista', data),
  reordenar: (ids) => api.post('/pendientes-lista/reordenar', { ids }),
  actualizar: (id, data) => api.patch(`/pendientes-lista/${id}`, data),
  eliminar: (id) => api.delete(`/pendientes-lista/${id}`)
};

// Servicios para usuarios (para autocomplete de menciones)
export const usuariosAPI = {
  // Buscar usuarios para menciones
  buscarUsuarios: (nombre) =>
    api.get('/user', { params: { nombre } }),
  
  // Obtener todos los usuarios activos
  getAllUsuarios: () =>
    api.get('/user', { params: { rol: 'all' } })
};

export default api;
